import { useMemo } from 'react';
import _ from 'lodash';
import { OrderType, OrderAction, TradingStrategyNameOrCustom, DebitOrCredit, AssetType } from '@tradingblock/types';
import { useOrderDataSelector } from '../state/OrderState';
import { useOrderPrice } from './useOrderPrice';

const calculateStrategyTotal = (
  isLimitOrder: boolean,
  orderType: OrderType,
  orderAction: OrderAction,
  ask: number,
  bid: number,
  quantity: number,
  totalMultiple: number,
  gcd: number,
  limitPrice: number | undefined,
  stopPrice: number | undefined,
  strategy: TradingStrategyNameOrCustom | undefined,
  debitOrCredit: DebitOrCredit,
  isBuy: boolean | undefined
) => {
  let orderPrice: number | undefined = 0;
  let execDebitOrCredit: DebitOrCredit | undefined = undefined;

  if (orderType === OrderType.Stop_Market) {
    orderPrice = stopPrice;
  } else if (orderType === OrderType.Stop_Limit) {
    orderPrice = limitPrice;
  } else if (isLimitOrder && limitPrice !== undefined) {
    if (isBuy || isBuy === undefined) {
      if (debitOrCredit === DebitOrCredit.Debit) {
        orderPrice = Math.min(ask, limitPrice);
      } else {
        orderPrice = Math.min(ask, -limitPrice);
      }
      if (orderPrice >= 0.0) {
        execDebitOrCredit = DebitOrCredit.Debit;
      } else {
        execDebitOrCredit = DebitOrCredit.Credit;
      }
    } else {
      if (debitOrCredit === DebitOrCredit.Credit) {
        orderPrice = Math.max(bid, limitPrice);
      } else {
        orderPrice = Math.max(bid, -limitPrice);
      }
      if (orderPrice >= 0.0) {
        execDebitOrCredit = DebitOrCredit.Credit;
      } else {
        execDebitOrCredit = DebitOrCredit.Debit;
      }
    }
  } else if (isLimitOrder) {
    orderPrice = undefined;
  } else {
    orderPrice = isBuy ? ask : bid;
  }

  const total = orderPrice && _.isFinite(orderPrice) ? quantity * orderPrice * totalMultiple * gcd : 0;
  return {
    total: Math.abs(total),
    execDebitOrCredit,
  };
};

export const useOrderTotal = () => {
  const orderAction = useOrderDataSelector(s => s.action);
  const orderType = useOrderDataSelector(s => s.orderType);
  const isLimitOrder = useOrderDataSelector(s => s.orderType === OrderType.Limit);
  const strategy = useOrderDataSelector(s => s.strategy);
  const match = useOrderDataSelector(s => s.validation && s.validation.match);
  const legs = useOrderDataSelector(s => s.legs);
  const isCustom = strategy === 'Custom';
  const isMatchCalendarStrategy =
    isCustom && match && (match.info.Name === 'Call Calendar' || match.info.Name === 'Put Calendar');
  const isCallVerticalMatch = isCustom && match && match.info.Name === 'Call Vertical';
  const isPutVerticalMatch = isCustom && match && match.info.Name === 'Put Vertical';
  const isCoveredCallMatch = isCustom && match && match.info.Name === 'Covered Call';
  const isCallButterflyMatch = match && match.info.Name === 'Call Butterfly';
  const isPutButterflyMatch = match && match.info.Name === 'Put Butterfly';
  const isIronButterflyMatch = match && match.info.Name === 'Iron Butterfly';
  const isIronCondorMatch = match && match.info.Name === 'Iron Condor';
  const isCallCondorMatch = match && match.info.Name === 'Call Condor';
  const isPutCondorMatch = match && match.info.Name === 'Put Condor';

  const latestExpiringOptionLeg = useMemo(() => {
    if (isMatchCalendarStrategy && legs) {
      return _.maxBy(Object.values(legs), l => l.Expiration && l.Expiration.date);
    }
    return undefined;
  }, [isMatchCalendarStrategy, legs]);

  const lowerStrikeLeg = useMemo(() => {
    if (
      (isCallVerticalMatch ||
        isPutVerticalMatch ||
        isCallButterflyMatch ||
        isPutButterflyMatch ||
        isIronButterflyMatch ||
        isIronCondorMatch ||
        isCallCondorMatch ||
        isPutCondorMatch) &&
      legs
    ) {
      return _.minBy(Object.values(legs), l => l.Strike);
    }
    return undefined;
  }, [
    isCallVerticalMatch,
    isPutVerticalMatch,
    isCallButterflyMatch,
    isPutButterflyMatch,
    isIronButterflyMatch,
    isIronCondorMatch,
    isCallCondorMatch,
    isPutCondorMatch,
    legs,
  ]);

  const shareLeg = useMemo(() => {
    if (isCoveredCallMatch && legs) {
      return _.find(Object.values(legs), l => l.AssetType === AssetType.Equity);
    }
    return undefined;
  }, [isCoveredCallMatch, legs]);
  const { quantity, debitCredit, price, stopPrice } = useOrderDataSelector(s => ({
    price: s.price,
    stopPrice: s.stopPrice,
    quantity: _.isFinite(s.quantity) ? s.quantity : 1,
    debitCredit: s.debitCredit,
  }));
  const isBuy = useMemo(() => {
    return !isCustom
      ? orderAction === OrderAction.Buy
      : isMatchCalendarStrategy
      ? orderAction === OrderAction.Buy
      : isCallVerticalMatch
      ? lowerStrikeLeg && lowerStrikeLeg.Action === OrderAction.Buy
      : isPutVerticalMatch
      ? lowerStrikeLeg && lowerStrikeLeg.Action === OrderAction.Sell
      : isCoveredCallMatch
      ? shareLeg && shareLeg.Action === OrderAction.Buy
      : isCallButterflyMatch
      ? lowerStrikeLeg && lowerStrikeLeg.Action === OrderAction.Buy
      : isPutButterflyMatch
      ? lowerStrikeLeg && lowerStrikeLeg.Action === OrderAction.Buy
      : isIronButterflyMatch
      ? lowerStrikeLeg && lowerStrikeLeg.Action === OrderAction.Buy
      : isIronCondorMatch
      ? lowerStrikeLeg && lowerStrikeLeg.Action === OrderAction.Sell
      : isCallCondorMatch
      ? lowerStrikeLeg && lowerStrikeLeg.Action === OrderAction.Buy
      : isPutCondorMatch
      ? lowerStrikeLeg && lowerStrikeLeg.Action === OrderAction.Buy
      : _.every(legs, l => l.Action === OrderAction.Buy) || _.some(legs, l => l.Action === OrderAction.Buy);
  }, [
    isCustom,
    orderAction,
    latestExpiringOptionLeg,
    lowerStrikeLeg,
    shareLeg,
    legs,
    isCallVerticalMatch,
    isPutVerticalMatch,
    isCoveredCallMatch,
    isCallButterflyMatch,
    isPutButterflyMatch,
    isIronButterflyMatch,
    isIronCondorMatch,
  ]);

  const { ask, bid, totalMultiple, gcd, marginCost } = useOrderPrice(isBuy);

  return useMemo(() => {
    const totalResults = calculateStrategyTotal(
      isLimitOrder,
      orderType,
      orderAction,
      ask,
      bid,
      quantity,
      totalMultiple,
      gcd,
      price !== undefined ? Math.abs(price) : price,
      stopPrice !== undefined ? Math.abs(stopPrice) : stopPrice,
      strategy,
      debitCredit === undefined ? DebitOrCredit.Debit : debitCredit,
      isBuy
    );

    return { ...totalResults, debitCredit, marginCost: marginCost * quantity * totalMultiple * gcd };
  }, [
    isLimitOrder,
    orderAction,
    ask,
    bid,
    quantity,
    totalMultiple,
    gcd,
    price,
    isCustom,
    debitCredit,
    stopPrice,
    isBuy,
    legs,
    strategy,
  ]);
};
